import React, { useEffect, useState } from 'react';
import {
  Layout,
  Typography,
  Row,
  Col,
  Card,
  Alert,
  Space,
  Tag,
  Button,
  Divider
} from 'antd';
import {
  AndroidOutlined,
  AppleOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  LaptopOutlined,
  MobileOutlined,
  QrcodeOutlined,
  SafetyCertificateOutlined,
  WindowsOutlined
} from '@ant-design/icons';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const ANDROID_METADATA_URL = '/downloads/android/latest.json';
const DEFAULT_ANDROID_METADATA = {
  available: false,
  versionName: '1.0.1',
  versionCode: '111',
  fileName: 'xiaolaba-android-1.0.1.apk',
  downloadUrl: '/downloads/android/xiaolaba-android-1.0.1.apk',
  sha256: '',
  size: '',
  updatedAt: ''
};

const WINDOWS_VERSION = '1.2.1';
const WINDOWS_FILE_NAME = 'jsxlb-setup-1.2.1-x64.exe';
const WINDOWS_DOWNLOAD_URL = `/desktop-downloads/${WINDOWS_FILE_NAME}`;
const WINDOWS_SHA256 = '50b06095bded61b03e477a2d866d1516d4a4493c3d8f55815e6fcb50eda8ca53';
const WINDOWS_ZIP_FILE_NAME = 'jsxlb-setup-1.2.1-x64.zip';
const WINDOWS_ZIP_DOWNLOAD_URL = `/desktop-downloads/${WINDOWS_ZIP_FILE_NAME}`;
const WINDOWS_ZIP_SHA256 = 'b71fadc1e76823d33efa85a50f6133967cd1e6263cd31390217371f79788a19e';
const WINDOWS_MICROSOFT_STORE_URL = 'ms-windows-store://pdp/?productid=9PK94DK6JCQB';
const WINDOWS_7_VERSION = '1.2.3';
const WINDOWS_7_X64_FILE_NAME = 'jsxlb-win7-1.2.3-x64-setup.exe';
const WINDOWS_7_X86_FILE_NAME = 'jsxlb-win7-1.2.3-ia32-setup.exe';
const WINDOWS_7_X64_DOWNLOAD_URL = `/desktop-downloads/${WINDOWS_7_X64_FILE_NAME}`;
const WINDOWS_7_X86_DOWNLOAD_URL = `/desktop-downloads/${WINDOWS_7_X86_FILE_NAME}`;
const WINDOWS_7_X64_SHA256 = 'cc5b176e666f48b4de4a02ce33de1665cb9e9d306b874a82597b45c7a75fcbec';
const WINDOWS_7_X86_SHA256 = '9dab1883de6f292dd904062db932cee1ec2449da5ac916131dd19230b9e9cab0';
const LINUX_VERSION = '1.2.3';
const KYLIN_FILE_NAME = 'jsxlb-kylin-1.2.3-amd64.deb';
const UOS_FILE_NAME = 'jsxlb-uos-1.2.3-amd64.deb';
const KYLIN_DOWNLOAD_URL = `/desktop-downloads/${KYLIN_FILE_NAME}`;
const UOS_DOWNLOAD_URL = `/desktop-downloads/${UOS_FILE_NAME}`;
const LINUX_SHA256 = 'd7f1c14c71d5654c8eca7ec319a0fb59662cc00678e5051ea54bff77d92549ab';
const TEACHER_WORKBENCH_VERSION = '1.0.2';
const TEACHER_WORKBENCH_FILE_NAME = 'xlb-teacher-workbench-1.0.2-x64.exe';
const TEACHER_WORKBENCH_DOWNLOAD_URL = `/desktop-downloads/teacher-workbench/${TEACHER_WORKBENCH_FILE_NAME}`;
const TEACHER_WORKBENCH_SHA256 = '8c58746bc5e513c1615fd70e9960bece983fd22be24c15ad918d06b74b5bc846';
const IOS_APPLE_ID = '6800961191';
const IOS_APP_STORE_URL = `https://apps.apple.com/cn/app/id${IOS_APPLE_ID}`;

function ClientCard({ icon, title, subtitle, accent, tags, children, actions }) {
  return (
    <Card
      style={{
        height: '100%',
        borderRadius: 24,
        overflow: 'hidden',
        border: `1px solid ${accent}25`,
        boxShadow: '0 18px 48px rgba(15, 23, 42, 0.08)'
      }}
      bodyStyle={{ padding: 0, height: '100%' }}
    >
      <div style={{ padding: '26px 26px 20px', background: `linear-gradient(135deg, ${accent}18, #fff 72%)` }}>
        <Space align="start" size={16}>
          <div style={{
            width: 58,
            height: 58,
            borderRadius: 18,
            display: 'grid',
            placeItems: 'center',
            color: accent,
            background: '#fff',
            fontSize: 28,
            boxShadow: '0 10px 26px rgba(15, 23, 42, 0.09)'
          }}>
            {icon}
          </div>
          <div>
            <Title level={3} style={{ margin: 0 }}>{title}</Title>
            <Paragraph style={{ margin: '8px 0 12px', color: '#475569' }}>{subtitle}</Paragraph>
            <Space wrap>
              {tags.map((tag) => <Tag key={tag} color="blue" style={{ borderRadius: 999 }}>{tag}</Tag>)}
            </Space>
          </div>
        </Space>
      </div>
      <div style={{ padding: '22px 26px 26px', display: 'flex', flexDirection: 'column', minHeight: 250 }}>
        <div style={{ flex: 1 }}>{children}</div>
        <Space wrap style={{ marginTop: 20 }}>{actions}</Space>
      </div>
    </Card>
  );
}

function DetailLine({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr', gap: 10, marginBottom: 10 }}>
      <Text type="secondary">{label}</Text>
      <Text style={{ wordBreak: 'break-all' }}>{children}</Text>
    </div>
  );
}

const InstallTutorialPage = () => {
  const [android, setAndroid] = useState(DEFAULT_ANDROID_METADATA);
  const [downloadStats, setDownloadStats] = useState({ total: null, today: null });
  const [clientDownloadsEnabled, setClientDownloadsEnabled] = useState(true);

  useEffect(() => {
    let active = true;
    fetch(`${ANDROID_METADATA_URL}?t=${Date.now()}`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((metadata) => {
        if (active) setAndroid({ ...DEFAULT_ANDROID_METADATA, ...metadata });
      })
      .catch(() => {
        if (active) setAndroid(DEFAULT_ANDROID_METADATA);
      });
    return () => { active = false; };
  }, []);

  const refreshDownloadStats = () => {
    fetch(`/api/v2/download/stats?t=${Date.now()}`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((result) => {
        const data = result?.data || result;
        const total = Number(data?.totalDownloads);
        const today = Number(data?.todayStats?.total_downloads ?? data?.today?.total);
        setDownloadStats({
          total: Number.isFinite(total) ? total : null,
          today: Number.isFinite(today) ? today : null
        });
      })
      .catch(() => setDownloadStats({ total: null, today: null }));
  };

  useEffect(() => {
    refreshDownloadStats();
  }, []);

  useEffect(() => {
    let active = true;
    fetch(`/api/v2/system-settings/public?t=${Date.now()}`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((result) => {
        const settings = result?.data || result || {};
        if (active) setClientDownloadsEnabled(settings.client_downloads_enabled !== false);
      })
      .catch(() => {
        // 配置接口短暂不可用时保持兼容，不误关正常下载。
      });
    return () => { active = false; };
  }, []);

  const handleAndroidDownload = () => {
    fetch('/api/v2/download/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientId: 'android-apk',
        clientVersion: android.versionName,
        downloadUrl: android.downloadUrl
      }),
      keepalive: true
    })
      .then((response) => {
        if (response.ok) refreshDownloadStats();
      })
      .catch(() => {
        // 下载不应因统计接口暂时不可用而中断。
      });
  };

  const handleTeacherWorkbenchDownload = () => {
    fetch('/api/v2/download/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientId: 'teacher-workbench-x64',
        clientVersion: TEACHER_WORKBENCH_VERSION
      }),
      keepalive: true
    })
      .then((response) => {
        if (response.ok) refreshDownloadStats();
      })
      .catch(() => {
        // 统计失败不能阻止安装包下载。
      });
  };

  const handleWindowsDownload = (clientId, downloadUrl, clientVersion = WINDOWS_VERSION) => {
    fetch('/api/v2/download/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientId,
        clientVersion,
        downloadUrl
      }),
      keepalive: true
    })
      .then((response) => {
        if (response.ok) refreshDownloadStats();
      })
      .catch(() => {
        // 统计失败不能阻止商店打开或安装包下载。
      });
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f7fb' }}>
      <PublicHeader />
      <Content style={{ padding: '24px 20px 52px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <section style={{
            borderRadius: 32,
            padding: '42px 34px',
            color: '#fff',
            background: 'radial-gradient(circle at 82% 10%, rgba(59,130,246,.40), transparent 30%), linear-gradient(135deg, #071a33, #0b3f72)',
            boxShadow: '0 24px 64px rgba(15, 23, 42, .18)',
            marginBottom: 28
          }}>
            <Tag style={{ borderRadius: 999, color: '#dbeafe', background: 'rgba(255,255,255,.10)', borderColor: 'rgba(255,255,255,.20)' }}>
              教室小喇叭官方发布
            </Tag>
            <Title level={1} style={{ color: '#fff', margin: '18px 0 12px' }}>
              <DownloadOutlined style={{ color: '#60a5fa', marginRight: 14 }} />
              客户端下载中心
            </Title>
            <Paragraph style={{ maxWidth: 760, color: 'rgba(255,255,255,.84)', fontSize: 17, lineHeight: 1.8, margin: 0 }}>
              请只从本页或学校管理员提供的官方链接下载。手机端、教室电脑端和微信小程序共用同一套线上服务与学校数据。
            </Paragraph>
            <Space wrap size={12} style={{ marginTop: 22 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                minHeight: 40,
                padding: '0 16px',
                border: '1px solid rgba(255,255,255,.18)',
                borderRadius: 999,
                background: 'rgba(255,255,255,.10)'
              }}>
                <DownloadOutlined style={{ color: '#60a5fa' }} />
                <span>累计下载</span>
                <strong>{downloadStats.total === null ? '--' : downloadStats.total.toLocaleString('zh-CN')}</strong>
                <span>次</span>
              </div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 40,
                padding: '0 16px',
                border: '1px solid rgba(255,255,255,.18)',
                borderRadius: 999,
                background: 'rgba(255,255,255,.07)'
              }}>
                今日下载&nbsp;<strong>{downloadStats.today === null ? '--' : downloadStats.today.toLocaleString('zh-CN')}</strong>&nbsp;次
              </div>
            </Space>
          </section>

          <Alert
            type="success"
            showIcon
            icon={<SafetyCertificateOutlined />}
            message="官方来源校验"
            description="官网使用 HTTPS；安卓安装包提供固定正式签名、版本号与 SHA-256 校验值。我们不会要求你关闭系统安全防护。"
            style={{ borderRadius: 18, marginBottom: 28 }}
          />

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <ClientCard
                icon={<AndroidOutlined />}
                title="Android 客户端"
                subtitle="适用于安卓手机和平板"
                accent="#16a34a"
                tags={[`版本 ${android.versionName}`, `构建 ${android.versionCode}`, '正式签名']}
                actions={[
                  <Button
                    key="android-download"
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    href={android.available ? android.downloadUrl : undefined}
                    onClick={android.available ? handleAndroidDownload : undefined}
                    disabled={!android.available}
                  >
                    {android.available ? '下载 Android APK' : '正式包生成中'}
                  </Button>
                ]}
              >
                <DetailLine label="安装包">{android.fileName}</DetailLine>
                <DetailLine label="文件大小">{android.size || '生成后公布'}</DetailLine>
                <DetailLine label="更新时间">{android.updatedAt || '生成后公布'}</DetailLine>
                <DetailLine label="SHA-256">{android.sha256 || '生成后公布'}</DetailLine>
                <Paragraph type="secondary" style={{ margin: '12px 0 0' }}>
                  首次安装需要允许浏览器安装未知来源应用，这是官网下载 APK 的 Android 系统确认流程；请同时核对域名和校验值。
                </Paragraph>
              </ClientCard>
            </Col>

            <Col xs={24} lg={12}>
              <ClientCard
                icon={<AppleOutlined />}
                title="iPhone / iPad 客户端"
                subtitle="适用于 iOS 和 iPadOS"
                accent="#111827"
                tags={['版本 1.0.1', 'App Store', '已上架']}
                actions={[
                  <Button
                    key="ios-download"
                    type="primary"
                    size="large"
                    icon={<AppleOutlined />}
                    href={IOS_APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    前往 App Store 下载
                  </Button>
                ]}
              >
                <Alert
                  type="success"
                  showIcon
                  message="已在中国大陆 App Store 正式上架"
                  description={`点击下方按钮前往 Apple 官方产品页下载。Apple ID：${IOS_APPLE_ID}；本页不提供企业签名或非官方 IPA。`}
                  style={{ borderRadius: 14 }}
                />
              </ClientCard>
            </Col>

            <Col xs={24} lg={12}>
              <ClientCard
                icon={<LaptopOutlined />}
                title="教师工作台 · Windows 版"
                subtitle="供教师个人电脑安装使用"
                accent="#7c3aed"
                tags={[`版本 ${TEACHER_WORKBENCH_VERSION}`, 'Windows 10/11', '64 位']}
                actions={[
                  <Button
                    key="teacher-workbench-x64"
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    href={clientDownloadsEnabled ? TEACHER_WORKBENCH_DOWNLOAD_URL : undefined}
                    download={clientDownloadsEnabled ? TEACHER_WORKBENCH_FILE_NAME : undefined}
                    onClick={clientDownloadsEnabled ? handleTeacherWorkbenchDownload : undefined}
                    disabled={!clientDownloadsEnabled}
                  >
                    {clientDownloadsEnabled ? `下载教师工作台 ${TEACHER_WORKBENCH_VERSION}` : '下载通道已关闭'}
                  </Button>
                ]}
              >
                <DetailLine label="安装包">{TEACHER_WORKBENCH_FILE_NAME}</DetailLine>
                <DetailLine label="文件大小">约 81.9 MB</DetailLine>
                <DetailLine label="系统要求">Windows 10/11，64 位</DetailLine>
                <DetailLine label="SHA-256">{TEACHER_WORKBENCH_SHA256}</DetailLine>
                <Paragraph>教师工作台用于教师登录后管理班级、广播、考勤、作业、成绩和其他日常工作。</Paragraph>
                <Paragraph type="secondary">当前安装程序未签名，安装时可能显示“未知发布者”；请核对官网域名、文件名和校验值。</Paragraph>
              </ClientCard>
            </Col>

            <Col xs={24} lg={12}>
              <ClientCard
                icon={<WindowsOutlined />}
                title="教室 Windows 客户端 · 完整安装版"
                subtitle="完整程序已包含在安装包中，不再依赖安装时二次下载"
                accent="#2563eb"
                tags={[`版本 ${WINDOWS_VERSION}`, 'Windows 10/11', '64 位']}
                actions={[
                  <Button
                    key="microsoft-store"
                    type="primary"
                    size="large"
                    icon={<WindowsOutlined />}
                    href={clientDownloadsEnabled ? WINDOWS_MICROSOFT_STORE_URL : undefined}
                    onClick={clientDownloadsEnabled
                      ? () => handleWindowsDownload('desktop-windows-microsoft-store', WINDOWS_MICROSOFT_STORE_URL)
                      : undefined}
                    disabled={!clientDownloadsEnabled}
                  >
                    {clientDownloadsEnabled ? '微软商店下载' : '下载通道已关闭'}
                  </Button>,
                  <Button
                    key="win64-zip"
                    size="large"
                    icon={<DownloadOutlined />}
                    href={clientDownloadsEnabled ? WINDOWS_ZIP_DOWNLOAD_URL : undefined}
                    download={clientDownloadsEnabled ? WINDOWS_ZIP_FILE_NAME : undefined}
                    onClick={clientDownloadsEnabled
                      ? () => handleWindowsDownload('desktop-windows-x64-zip', WINDOWS_ZIP_DOWNLOAD_URL)
                      : undefined}
                    disabled={!clientDownloadsEnabled}
                  >
                    下载 ZIP 压缩版
                  </Button>,
                  <Button
                    key="win64-exe"
                    size="large"
                    icon={<DownloadOutlined />}
                    href={clientDownloadsEnabled ? WINDOWS_DOWNLOAD_URL : undefined}
                    download={clientDownloadsEnabled ? WINDOWS_FILE_NAME : undefined}
                    onClick={clientDownloadsEnabled
                      ? () => handleWindowsDownload('desktop-windows-x64-exe', WINDOWS_DOWNLOAD_URL)
                      : undefined}
                    disabled={!clientDownloadsEnabled}
                  >
                    下载 EXE 安装版
                  </Button>
                ]}
              >
                {!clientDownloadsEnabled && (
                  <Alert
                    type="warning"
                    showIcon
                    message="管理员暂时关闭了桌面客户端和教师工作台下载"
                    description="已经安装的客户端不受影响；需要新安装时请联系学校管理员。"
                    style={{ borderRadius: 14, marginBottom: 16 }}
                  />
                )}
                <DetailLine label="安装包">{WINDOWS_FILE_NAME}</DetailLine>
                <DetailLine label="文件大小">约 73.9 MB</DetailLine>
                <DetailLine label="ZIP 压缩包">{WINDOWS_ZIP_FILE_NAME}</DetailLine>
                <DetailLine label="ZIP SHA-256">{WINDOWS_ZIP_SHA256}</DetailLine>
                <DetailLine label="系统要求">Windows 10/11，64 位</DetailLine>
                <DetailLine label="SHA-256">{WINDOWS_SHA256}</DetailLine>
                <DetailLine label="数字签名">当前安装程序未签名，可能出现发布者未知或下载信誉提示。</DetailLine>
                <Paragraph>推荐优先使用微软商店；也可以下载ZIP压缩包，解压后运行其中的完整安装程序。EXE与ZIP均不会在安装过程中再次下载程序主体。</Paragraph>
                <Paragraph type="secondary">
                  Windows 对新发布或信誉尚少的安装程序可能显示 SmartScreen 提示。请确认下载地址、文件名和学校管理员通知一致；若系统明确判定为恶意文件，请停止安装并联系我们。
                </Paragraph>
              </ClientCard>
            </Col>

            <Col xs={24} lg={12}>
              <ClientCard
                icon={<WindowsOutlined />}
                title="教室 Windows 7 客户端 · 兼容版"
                subtitle="仅供无法升级系统的教室电脑使用，请按系统位数选择安装包"
                accent="#d97706"
                tags={[`版本 ${WINDOWS_7_VERSION}`, 'Windows 7 SP1', '32 位 / 64 位']}
                actions={[
                  <Button
                    key="win7-x64"
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    href={clientDownloadsEnabled ? WINDOWS_7_X64_DOWNLOAD_URL : undefined}
                    download={clientDownloadsEnabled ? WINDOWS_7_X64_FILE_NAME : undefined}
                    onClick={clientDownloadsEnabled
                      ? () => handleWindowsDownload('desktop-windows7-x64-exe', WINDOWS_7_X64_DOWNLOAD_URL, WINDOWS_7_VERSION)
                      : undefined}
                    disabled={!clientDownloadsEnabled}
                  >
                    {clientDownloadsEnabled ? '下载 Win7 64 位安装包' : '下载通道已关闭'}
                  </Button>,
                  <Button
                    key="win7-x86"
                    size="large"
                    icon={<DownloadOutlined />}
                    href={clientDownloadsEnabled ? WINDOWS_7_X86_DOWNLOAD_URL : undefined}
                    download={clientDownloadsEnabled ? WINDOWS_7_X86_FILE_NAME : undefined}
                    onClick={clientDownloadsEnabled
                      ? () => handleWindowsDownload('desktop-windows7-ia32-exe', WINDOWS_7_X86_DOWNLOAD_URL, WINDOWS_7_VERSION)
                      : undefined}
                    disabled={!clientDownloadsEnabled}
                  >
                    下载 Win7 32 位安装包
                  </Button>
                ]}
              >
                <DetailLine label="系统要求">Windows 7 SP1；建议安装 KB4490628、KB4474419 后重启</DetailLine>
                <DetailLine label="64 位安装包">{WINDOWS_7_X64_FILE_NAME}（78.2 MB）</DetailLine>
                <DetailLine label="64 位 SHA-256">{WINDOWS_7_X64_SHA256}</DetailLine>
                <DetailLine label="32 位安装包">{WINDOWS_7_X86_FILE_NAME}（74.3 MB）</DetailLine>
                <DetailLine label="32 位 SHA-256">{WINDOWS_7_X86_SHA256}</DetailLine>
                <Paragraph type="secondary" style={{ margin: '12px 0 0' }}>
                  兼容版使用独立的应用标识和本地数据目录，不会覆盖普通版配置。安装包目前未签名，可能显示“未知发布者”；请核对官网域名和校验值。Windows 7 与该兼容版所用的 Electron 22 均已停止官方安全维护，建议有条件时升级系统。
                </Paragraph>
              </ClientCard>
            </Col>

            <Col xs={24} lg={12}>
              <ClientCard
                icon={<LaptopOutlined />}
                title="麒麟 / 统信 UOS · Linux 客户端"
                subtitle="适用于采用 DEB 安装包的 x86_64 桌面系统"
                accent="#0891b2"
                tags={[`版本 ${LINUX_VERSION}`, '麒麟 / 统信 UOS', 'amd64']}
                actions={[
                  <Button
                    key="kylin-amd64"
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    href={clientDownloadsEnabled ? KYLIN_DOWNLOAD_URL : undefined}
                    download={clientDownloadsEnabled ? KYLIN_FILE_NAME : undefined}
                    onClick={clientDownloadsEnabled
                      ? () => handleWindowsDownload('desktop-kylin-amd64-deb', KYLIN_DOWNLOAD_URL, LINUX_VERSION)
                      : undefined}
                    disabled={!clientDownloadsEnabled}
                  >
                    {clientDownloadsEnabled ? '下载麒麟 x86_64 安装包' : '下载通道已关闭'}
                  </Button>,
                  <Button
                    key="uos-amd64"
                    size="large"
                    icon={<DownloadOutlined />}
                    href={clientDownloadsEnabled ? UOS_DOWNLOAD_URL : undefined}
                    download={clientDownloadsEnabled ? UOS_FILE_NAME : undefined}
                    onClick={clientDownloadsEnabled
                      ? () => handleWindowsDownload('desktop-uos-amd64-deb', UOS_DOWNLOAD_URL, LINUX_VERSION)
                      : undefined}
                    disabled={!clientDownloadsEnabled}
                  >
                    下载统信 UOS x86_64 安装包
                  </Button>
                ]}
              >
                <DetailLine label="麒麟包">{KYLIN_FILE_NAME}（106.9 MB）</DetailLine>
                <DetailLine label="统信包">{UOS_FILE_NAME}（106.9 MB）</DetailLine>
                <DetailLine label="SHA-256">{LINUX_SHA256}</DetailLine>
                <Paragraph type="secondary" style={{ margin: '12px 0 0' }}>
                  两个下载文件内容相同，均为 Debian 格式的 amd64 安装包；请按系统名称下载。ARM、龙芯等非 x86_64 设备不能使用。不同麒麟/统信发行版的依赖环境可能有差异，请先在目标设备上试装验证。
                </Paragraph>
              </ClientCard>
            </Col>

            <Col xs={24} lg={12}>
              <ClientCard
                icon={<QrcodeOutlined />}
                title="微信小程序"
                subtitle="无需安装，微信扫码即可使用"
                accent="#07c160"
                tags={['教师端', '家长端', '线上服务器']}
                actions={[
                  <Button key="wechat" size="large" icon={<MobileOutlined />} href="/wechat">
                    查看小程序说明
                  </Button>
                ]}
              >
                <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                  <img src="/wechatlogo.jpg" alt="教室小喇叭微信小程序码" style={{ width: 132, height: 132, objectFit: 'cover', borderRadius: 16 }} />
                  <Paragraph style={{ maxWidth: 300, margin: 0 }}>
                    使用微信扫描小程序码，按学校提供的教师口令、班级口令或账号登录。
                  </Paragraph>
                </div>
              </ClientCard>
            </Col>
          </Row>

          <Divider />

          <Card style={{ borderRadius: 20 }}>
            <Title level={3}><CheckCircleOutlined style={{ color: '#16a34a', marginRight: 10 }} />下载与安装安全说明</Title>
            <Row gutter={[24, 16]}>
              <Col xs={24} md={8}>
                <Text strong>1. 认准官方域名</Text>
                <Paragraph type="secondary">下载页面应为 xlb.810086.com，并使用 HTTPS。</Paragraph>
              </Col>
              <Col xs={24} md={8}>
                <Text strong>2. 核对版本与校验值</Text>
                <Paragraph type="secondary">安卓正式包发布后，以本页显示的 SHA-256 为准。</Paragraph>
              </Col>
              <Col xs={24} md={8}>
                <Text strong>3. 不绕过明确拦截</Text>
                <Paragraph type="secondary">若安全软件明确报告恶意行为，请停止安装并联系页面底部的技术支持。</Paragraph>
              </Col>
            </Row>
          </Card>
        </div>
      </Content>
      <PublicFooter />
    </Layout>
  );
};

export default InstallTutorialPage;
